/* eslint no-use-before-define: 0 */
import { newSearchDone$ } from "@sharedFrontend/globalEvents";

import Rx from "rxjs/Rx";
import ErrorService from "../services/error.service";
import MarkerManager from "../services/marker-manager.service";
import ApartmentPopupComponent from "./apartment-popup.component";

const mapClickedSubject = new Rx.Subject();

export default class SearchMapComponent {
    /**
     *
     * @param {Object} jqNode - Jquery node
     */
    constructor(jqNode) {
        this.jqNode = jqNode;
        this.args = null;
        this.innerMap = null;
        this.errorService = new ErrorService(jqNode);
        this.markerManager = new MarkerManager(this);
        this.maxZoom = 15;
        this.mapClicked$ = mapClickedSubject.asObservable();
        this.apartmentPropertyPopup = null;

        this.initialize();
    }

    /**
     * @private
     */
    initialize() {
        this.parseArgsFromHTML();
        this.errorService.initDisplayingGoogleMapsError();
        this.initInnerMap();
        this.initEventHandlers();

        this.apartmentPropertyPopup = new ApartmentPopupComponent(this.innerMap, this.args, this.mapClicked$);
    }

    /**
     * @private
     */
    initEventHandlers() {
        newSearchDone$.subscribe(places => this.displaySearchResults(places.pages));

        this.innerMap.addListener("click", () => {
            mapClickedSubject.next();
        });

        // eslint-disable-next-line no-undef
        $(window).resize(() => {
            this.rezoomToDisplayAllMarkers();
        });
    }

    /**
     * @private
     */
    displaySearchResults(places) {
        this.markerManager.clearMarkers();

        this.markerManager.createMarkersFromPlaces(places);
        if (this.markerManager.markers.length) {
            this.rezoomToDisplayAllMarkers();
            this.markerManager.displayMarkers();
        } else {
            this.setDefaultCenter();
            this.setDefaultZoom();
        }
    }

    /**
     * @private
     */
    rezoomToDisplayAllMarkers() {
        if (this.markerManager.markers.length) {
            /* eslint max-len: 0 */
            /**
             * Google Maps works not as expected with fitbounds when the map is hidden.
             * So wee need to do 'fitting' later when map will be visible ~ when it be resized (tablet will change orietation).
             * Solution was taken from this link:
             * https://stackoverflow.com/questions/11098700/fitbounds-shows-whole-earth-if-map-is-first-hidden-and-then-shown
             */
            const isMapHidden = this.jqNode.css("display") === "none";

            if (isMapHidden) {
                window.google.maps.event.addListenerOnce(this.innerMap, "resize", () => {
                    this.fitInMarkersBounds();
                });

                return;
            }

            this.fitInMarkersBounds();
        } else {
            this.setDefaultCenter();
            this.decreaseZoomIfTooMuch();
        }
    }

    /**
     * @private
     */
    fitInMarkersBounds() {
        /**
         * This is important. fitBounds will set very large zoom and we want to limit it.
         * We will unset maxZoom when map will be ready or, in GoogleMaps terms,
         * when it will have 'idle' state.
         */
        this.innerMap.setOptions({ maxZoom: this.maxZoom });

        const bounds = this.markerManager.boundsForMarkers;
        this.innerMap.setCenter(bounds.getCenter());
        this.innerMap.fitBounds(bounds);

        window.google.maps.event.addListenerOnce(this.innerMap, "idle", () => {
            this.innerMap.setOptions({ maxZoom: undefined });
        });
    }

    /**
     * @private
     */
    decreaseZoomIfTooMuch() {
        if (this.innerMap.getZoom() > this.args.zoom) {
            this.innerMap.setZoom(this.args.zoom);
        }
    }

    /**
     * @private
     */
    setDefaultCenter() {
        this.innerMap.setCenter({ lat: this.args.centerLat, lng: this.args.centerLng });
    }

    /**
     * @private
     */
    setDefaultZoom() {
        this.innerMap.setZoom(this.args.zoom);
    }

    /**
     * @private
     */
    parseArgsFromHTML() {
        this.args = {
            zoom: this.jqNode.data("zoom"),
            centerLat: this.jqNode.data("center-lat"),
            centerLng: this.jqNode.data("center-lng"),
            markerIconUrl: this.jqNode.data("marker-icon-url"),
            markerActiveIconUrl: this.jqNode.data("marker-active-icon-url"),
            apartmentMode: this.jqNode.data("apartments-mode"),
            markerApartmentAvailableUrl: this.jqNode.data("marker-apartment-available-url"),
            markerApartmentAvailableActiveUrl: this.jqNode.data("marker-apartment-available-active-url"),
            markerApartmentNoAvailableUrl: this.jqNode.data("marker-apartment-noavailable-url"),
            markerApartmentNoAvailableActiveUrl: this.jqNode.data("marker-apartment-noavailable-active-url"),
            textApartmentAmountFreeApartments: this.jqNode.data("text-apartment-amount-free-apartments"),
            textApartmentAmountOtherApartments: this.jqNode.data("text-apartment-amount-other-apartments"),
            textApartmentApartmentPropertyLink: this.jqNode.data("text-apartment-apartment-property-link"),
            textApartmentNoFreeApartments: this.jqNode.data("text-apartment-no-free-apartments"),
        };

        if (this.args.centerLat) {
            this.args.centerLat = parseFloat(this.args.centerLat.replace(",", "."));
        }

        if (this.args.centerLng) {
            this.args.centerLng = parseFloat(this.args.centerLng.replace(",", "."));
        }

        if (this.args.zoom) {
            this.args.zoom = parseInt(this.args.zoom, 10);
        }
    }

    /**
     * @private
     */
    initInnerMap() {
        this.innerMap = new window.google.maps.Map(this.jqNode.get(0), {
            zoom: this.args.zoom,
            center: { lat: this.args.centerLat, lng: this.args.centerLng },
            gestureHandling: "greedy",
            keyboardShortcuts: true,
        });

        window.google.maps.event.addListenerOnce(this.innerMap, "idle", () => {
            const { jqNode } = this;
            // eslint-disable-next-line no-undef
            $(
                jqNode
                    .parent()
                    .parent(),
            ).stickySidebar({
                topSpacing: 0,
                containerSelector: ".row",
                innerWrapperSelector: ".search-map-js-component-wrapper",
            });
        });
    }
}
